import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Clock, AlertTriangle, ShieldAlert, ShieldCheck, ArrowRight } from "lucide-react";

/**
 * Phase 5D — Late Coming Widget
 *
 * Shows monthly grace_late / hard_late summary across white-collar employees.
 *  - Tier 1 (grace 9:31-9:45): first 8 free per month
 *  - Tier 2 (hard >9:45): immediate ½ day each
 *
 * Surfaces top-offending employees first. Click row → employee profile.
 */
const STATUS_META = {
  critical: { label: "Critical",   cls: "bg-red-100 text-red-800 border-red-200",       Icon: ShieldAlert },
  exceeded: { label: "Exceeded",   cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: AlertTriangle },
  watch:    { label: "Watch",      cls: "bg-yellow-50 text-yellow-800 border-yellow-200", Icon: Clock },
  clean:    { label: "Clean",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck },
};

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function LateComingWidget() {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // search by name/punch_id

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/attendance/late-summary", { params: { month: `${month}-01` } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ rows: [], totals: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return data.rows;
    return data.rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q)
      || (r.punch_id || "").toLowerCase().includes(q)
    );
  }, [data, filter]);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="late-coming-widget">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <Clock className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Late Coming</div>
              <h3 className="text-base font-semibold text-slate-900 leading-tight" style={{ fontFamily: "Chivo" }}>
                Monthly Late Summary
              </h3>
              <p className="text-[11px] text-slate-500">Tier 1: 8 grace passes · Tier 2: ½ day each</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-8 w-36 text-xs"
              data-testid="late-month-picker"
            />
          </div>
        </div>

        {/* Top-level KPI tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          <KPI label="Employees" value={data?.totals?.employees ?? "—"} tone="slate" />
          <KPI label="Grace (Tier 1)" value={data?.totals?.grace_late ?? "—"} tone="yellow" />
          <KPI label="Hard (Tier 2)" value={data?.totals?.hard_late ?? "—"} tone="red" />
          <KPI label="½-day Deductions" value={data?.totals?.late_deduction_days ?? "—"} tone="pink" />
        </div>

        <div className="mt-3 flex items-center gap-2">
          <Input
            placeholder="Filter by name or punch ID…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 text-xs"
            data-testid="late-filter-input"
          />
          <Link
            to="/attendance"
            className="text-[11px] text-red-600 hover:underline flex-shrink-0 flex items-center gap-1"
            data-testid="late-view-all"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Table */}
        <div className="mt-3 max-h-80 overflow-y-auto border border-slate-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0 z-10 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-right px-3 py-2">Grace</th>
                <th className="text-right px-3 py-2">Hard</th>
                <th className="text-right px-3 py-2">½-day</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan="5" className="text-center py-6 text-slate-400">Loading…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan="5" className="text-center py-6 text-slate-400">
                  {filter ? "No matches" : "No data — upload monthly attendance to see this widget."}
                </td></tr>
              )}
              {!loading && filteredRows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.clean;
                const Icon = meta.Icon;
                return (
                  <tr key={r.employee_id} className="hover:bg-red-50/40" data-testid={`late-row-${r.punch_id}`}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/employees/${r.employee_id}`}
                        className="font-medium text-slate-900 hover:text-red-700 hover:underline"
                      >
                        {r.name || "—"}
                      </Link>
                      <div className="text-[10px] text-slate-500 font-mono">{r.punch_id}</div>
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${r.grace_late > 8 ? "text-amber-700 font-semibold" : ""}`}>
                      {r.grace_late}/8
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${r.hard_late > 0 ? "text-red-700 font-semibold" : "text-slate-400"}`}>
                      {r.hard_late}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-red-700">
                      {r.late_deduction_days || 0}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${meta.cls}`}>
                        <Icon className="h-2.5 w-2.5" />
                        {meta.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function KPI({ label, value, tone }) {
  const cls = {
    slate:  "bg-slate-50 text-slate-900 border-slate-200",
    yellow: "bg-yellow-50 text-yellow-900 border-yellow-200",
    red:    "bg-red-50 text-red-900 border-red-200",
    pink:   "bg-red-50 text-red-900 border-red-200",
  }[tone] || "bg-slate-50 text-slate-900 border-slate-200";
  return (
    <div className={`rounded border ${cls} px-2 py-1.5`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-bold font-mono leading-tight" style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
    </div>
  );
}
